/**
 * React Query hooks for dashboard sharing — mirrors hooks/use-projects.ts
 * participant section. Same UX (toast on success/error, invalidate the
 * participants list + the parent dashboards list).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { ParticipantRole } from "@/services/project-service"

import { queryKeys } from "@/lib/query-keys"
import { SettingsService } from "@/services/settings-service"

import { DashboardService } from "../services/dashboard-service"

export function useDashboardParticipants(dashboardId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.dashboardParticipants(dashboardId || ""),
    queryFn: () => DashboardService.listParticipants(dashboardId!),
    enabled: !!dashboardId,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  })
}

export function useAddDashboardParticipant() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      dashboardId,
      participant,
    }: {
      dashboardId: string
      participant: {
        oid: string
        displayName: string
        type: "user" | "group"
        role: ParticipantRole
      }
    }) => DashboardService.addParticipant(dashboardId, participant),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardParticipants(variables.dashboardId),
      })
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboards })
      toast.success(
        t("dash.sharing.add_success", { defaultValue: "Participant added successfully" })
      )
    },
    onError: (error: Error) => {
      toast.error(t("dash.sharing.add_error", { defaultValue: "Failed to add participant" }), {
        description: error.message,
      })
    },
  })
}

export function useUpdateDashboardParticipantRole() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      dashboardId,
      participantOid,
      role,
    }: {
      dashboardId: string
      participantOid: string
      role: ParticipantRole
    }) => DashboardService.updateParticipantRole(dashboardId, participantOid, role),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardParticipants(variables.dashboardId),
      })
      toast.success(
        t("dash.sharing.role_update_success", { defaultValue: "Role updated successfully" })
      )
    },
    onError: (error: Error) => {
      toast.error(t("dash.sharing.role_update_error", { defaultValue: "Failed to update role" }), {
        description: error.message,
      })
    },
  })
}

export function useRemoveDashboardParticipant() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const currentUserOid = SettingsService.getInstance().claims.oid

  return useMutation({
    mutationFn: ({
      dashboardId,
      participantOid,
    }: {
      dashboardId: string
      participantOid: string
    }) => DashboardService.removeParticipant(dashboardId, participantOid),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dashboardParticipants(variables.dashboardId),
      })
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboards })
      const isSelfLeave = variables.participantOid === currentUserOid
      toast.success(
        isSelfLeave
          ? t("dash.sharing.leave_success", { defaultValue: "You have left this dashboard" })
          : t("dash.sharing.remove_success", {
              defaultValue: "Participant removed successfully",
            })
      )
    },
    onError: (error: Error) => {
      toast.error(
        t("dash.sharing.remove_error", { defaultValue: "Failed to remove participant" }),
        { description: error.message }
      )
    },
  })
}

export function useMarkDashboardViewed() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (dashboardId: string) => DashboardService.markViewed(dashboardId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboards })
    },
  })
}
